<script setup lang="ts">
import type { NoviceView } from '~/features/my-wallet/types/receiving-model'
import { SolButton } from '@solfacil/girassol'
import { I18nT, useI18n } from 'vue-i18n'
import IconStar from '~icons/material-symbols/star'
import IconWarning from '~icons/material-symbols/warning-outline'
import FaqAccordion from '~/features/my-wallet/blocks/faq/FaqAccordion.vue'
import EssentialBanner from '~/features/my-wallet/blocks/receiving-model/EssentialBanner.vue'
import ModelCard from '~/features/my-wallet/blocks/receiving-model/ModelCard.vue'
import NoviceProgress from '~/features/my-wallet/blocks/receiving-model/NoviceProgress.vue'
import PerformanceSummary from '~/features/my-wallet/blocks/receiving-model/PerformanceSummary.vue'
import ReceivingModelSkeleton from '~/features/my-wallet/blocks/receiving-model/ReceivingModelSkeleton.vue'
import BlockError from '~/features/my-wallet/components/BlockError.vue'
import CardCarousel from '~/features/my-wallet/components/CardCarousel.vue'
import MyWalletNotImpersonate from '~/features/my-wallet/components/MyWalletNotImpersonate.vue'
import MyWalletStateGate from '~/features/my-wallet/components/MyWalletStateGate.vue'
import { useReceivingModel } from '~/features/my-wallet/composables/useReceivingModel'
import { MY_WALLET_ANCHORS } from '~/features/my-wallet/constants/my-wallet'

const { t } = useI18n()

const { screenState, displayData, handleRefresh, openImpersonateModal } = useReceivingModel()

const performanceSection = useTemplateRef<HTMLElement>('performanceSection')
const faqAccordion = useTemplateRef<InstanceType<typeof FaqAccordion>>('faqAccordion')

function performanceSubtitleKey(novice: NoviceView | null) {
  if (!novice)
    return 'my_wallet.receiving_model.performance.subtitle'
  return novice.isDone ? 'my_wallet.receiving_model.novice.subtitle_done' : 'my_wallet.receiving_model.novice.subtitle'
}

function scrollToSection(section: HTMLElement | null | undefined) {
  section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  section?.focus({ preventScroll: true })
}
</script>

<template>
  <MyWalletNotImpersonate v-if="screenState === 'not_impersonating'" @open-impersonate="openImpersonateModal" />

  <MyWalletStateGate
    v-else
    :state="screenState"
    :data="displayData"
    error-id="my-wallet-receiving-model-error"
    @retry="handleRefresh"
  >
    <template #skeleton>
      <ReceivingModelSkeleton />
    </template>

    <template #default="{ data: receivingModel }">
      <section class="receiving-model" aria-labelledby="my-wallet-receiving-model-title">
        <header class="receiving-model-header">
          <div class="receiving-model-heading">
            <h3 id="my-wallet-receiving-model-title" class="receiving-model-title">
              {{ t('my_wallet.receiving_model.title') }}
            </h3>
            <p class="receiving-model-updated">
              {{ t('my_wallet.updated_at', receivingModel.updatedAt) }}
            </p>
          </div>
          <div class="receiving-model-actions">
            <SolButton
              id="my-wallet-how-it-works"
              class="receiving-model-action"
              variant="secondary"
              size="medium"
              @click="scrollToSection(faqAccordion?.rootElement)"
            >
              {{ t('my_wallet.receiving_model.how_it_works') }}
            </SolButton>
            <SolButton
              v-if="!receivingModel.isEssentialProfile"
              id="my-wallet-see-performance"
              class="receiving-model-action"
              size="medium"
              @click="scrollToSection(performanceSection)"
            >
              {{ t('my_wallet.receiving_model.see_performance') }}
            </SolButton>
          </div>
        </header>

        <div v-if="receivingModel.hasCommercialException" class="receiving-model-banner -commercial" role="status">
          <IconStar class="receiving-model-banner-icon" aria-hidden="true" />
          <p>
            <strong>{{ t('my_wallet.receiving_model.commercial_exception_banner.title') }}</strong>
            {{ t('my_wallet.receiving_model.commercial_exception_banner.description') }}
          </p>
        </div>

        <div v-if="receivingModel.delinquencyBanner" class="receiving-model-banner -warning" role="status">
          <IconWarning class="receiving-model-banner-icon" aria-hidden="true" />
          <I18nT keypath="my_wallet.receiving_model.delinquency_banner.text" tag="p">
            <template #model>
              {{ receivingModel.delinquencyBanner.model }}
            </template>
            <template #highlight>
              <strong class="receiving-model-underline">{{ t('my_wallet.receiving_model.delinquency_banner.highlight') }}</strong>
            </template>
          </I18nT>
        </div>

        <BlockError
          v-if="!receivingModel.cards"
          id="my-wallet-models-error"
          @retry="handleRefresh"
        />
        <CardCarousel
          v-else
          :item-count="receivingModel.cards.items.length + (receivingModel.isEssentialProfile ? 1 : 0)"
          :start-index="receivingModel.cards.startIndex"
          :label="t('my_wallet.receiving_model.cards_aria')"
          :essential="receivingModel.isEssentialProfile"
        >
          <template #default="{ index }">
            <EssentialBanner v-if="index === receivingModel.cards.items.length" />
            <ModelCard
              v-else
              :card="receivingModel.cards.items[index]"
              :mode="receivingModel.cards.defaultModelMode"
              @see-details="scrollToSection(performanceSection)"
              @retry="handleRefresh"
            />
          </template>
        </CardCarousel>

        <template v-if="!receivingModel.isEssentialProfile">
          <hr class="receiving-model-divider">

          <section
            :id="MY_WALLET_ANCHORS.PERFORMANCE"
            ref="performanceSection"
            class="receiving-model-section"
            tabindex="-1"
            aria-labelledby="my-wallet-performance-title"
          >
            <div class="receiving-model-section-heading">
              <h3 id="my-wallet-performance-title" class="receiving-model-section-title">
                {{ t('my_wallet.receiving_model.performance.title') }}
              </h3>
              <p class="receiving-model-section-subtitle">
                {{ t(performanceSubtitleKey(receivingModel.novice)) }}
              </p>
            </div>

            <NoviceProgress v-if="receivingModel.novice" :novice="receivingModel.novice" />
            <BlockError
              v-else-if="!receivingModel.performance"
              id="my-wallet-performance-error"
              @retry="handleRefresh"
            />
            <PerformanceSummary v-else :performance="receivingModel.performance" />
          </section>
        </template>

        <hr class="receiving-model-divider">

        <FaqAccordion
          :id="MY_WALLET_ANCHORS.FAQ"
          ref="faqAccordion"
          :title="t('my_wallet.faq.title')"
          :items="receivingModel.faqItems"
        />
      </section>
    </template>
  </MyWalletStateGate>
</template>

<style scoped lang="scss">
@use '../../styles/typography';

.receiving-model {
  @apply flex flex-col gap-6 w-full;

  .receiving-model-header {
    @apply flex flex-col gap-4;

    @screen sm:system {
      @apply flex-row items-start justify-between gap-6;
    }
  }

  .receiving-model-heading {
    @apply flex flex-col gap-1;
  }

  .receiving-model-title {
    @apply fonts-heading-h3 text-neutral-low-dark;
    line-height: 1.5;

    @screen sm:system {
      @apply text-sm;
    }
  }

  .receiving-model-updated {
    @include typography.updated-at;
  }

  .receiving-model-actions {
    @apply flex flex-wrap items-center gap-2;

    @screen sm:system {
      @apply flex-shrink-0;
    }
  }

  .receiving-model-action {
    @apply flex-1 whitespace-nowrap;

    @screen sm:system {
      @apply flex-none;
    }
  }

  .receiving-model-banner {
    @apply flex items-start gap-2.5 px-4 py-3.5 rounded-md border;
    @apply fonts-body-medium-regular text-neutral-low-dark;

    &.-commercial {
      @apply border-brand-secondary-medium;
      @apply bg-brand-secondary-light;

      .receiving-model-banner-icon {
        @apply text-brand-primary-medium;
      }
    }

    &.-warning {
      @apply items-center bg-feedback-warning-light border-feedback-warning-pure text-feedback-warning-dark;
    }
  }

  .receiving-model-banner-icon {
    @apply w-4 h-4 flex-shrink-0 mt-0.5;
  }

  .receiving-model-underline {
    @apply underline;
  }

  .receiving-model-divider {
    @apply w-full h-px my-2 border-0 bg-neutral-high-medium;
  }

  .receiving-model-section {
    @apply flex flex-col gap-6;

    &:focus {
      @apply outline-none;
    }
  }

  .receiving-model-section-heading {
    @apply flex flex-col gap-1;
  }

  .receiving-model-section-title {
    @apply text-neutral-low-dark;
    @include typography.title-20(1.5);
  }

  .receiving-model-section-subtitle {
    @apply fonts-body-large-regular text-neutral-low-medium;
    line-height: 1.5;
  }
}
</style>
